import {YslabJeffAppApartment} from "../detail/planner3D/components/types/YslabEventTypes";

export enum Planner3DTypes {
    YSLAB,
    YSLAB_ALLINONE
}

export const getPlanner3dData = (offerId: number) => planner3dOfferMap[offerId] || null;

/**
 * RP and YSLAB may have different ways of naming properties. By default before comparison we strip characters other than letters and numbers
 */
export const compareJeffProperties =
    (
        apartment: Pick<YslabJeffAppApartment, "configuredBuildingName" | "buildingNumber" | "mappingName">,
        allowComparisonBasedOnConfiguredBuildingName = false,
        /**
         * @param buildingNameMap - remaps yslab buildingNumber to the ones used in our apartment numbers
         */
        buildingNameMap?: Record<string, string>
    ) =>
    (p: {number: string}) => {
        const {configuredBuildingName, buildingNumber, mappingName} = apartment;
        // match buildings
        let isBuildingMatched = !Boolean(buildingNumber);
        const canUseConfiguredBuildingName = allowComparisonBasedOnConfiguredBuildingName || (configuredBuildingName && !buildingNumber);
        const remappedBuildingNumber = (buildingNumber && buildingNameMap?.[buildingNumber]) || buildingNumber;

        if (remappedBuildingNumber || configuredBuildingName) {
            const rpBuildingMatch =
                (!!remappedBuildingNumber && p.number.match(remappedBuildingNumber)) ||
                (canUseConfiguredBuildingName && !!configuredBuildingName && p.number.match(configuredBuildingName));
            isBuildingMatched = Boolean(rpBuildingMatch);
        }
        // match apartments
        const rpApartmentNumber = stripJeffNamesToLastNumber(remappedBuildingNumber ? p.number.replace(remappedBuildingNumber, "") : p.number);
        const mappingNameWithoutBuilding = remappedBuildingNumber ? mappingName.replace(remappedBuildingNumber, "") : mappingName;
        const yslabApartmentNumber = stripJeffNamesToLastNumber(mappingNameWithoutBuilding);
        return isBuildingMatched && yslabApartmentNumber?.toLowerCase() === rpApartmentNumber.toLowerCase();
    };
const stripJeffNamesToLastNumber = (str: string) => {
    const match = str.match(/[0-9]*$/);
    // remove leading zeros
    return match ? parseInt(match[0], 10).toString() : str;
};

const planner3dOfferMap: Record<number, Planner3DDefinitions> = {
    16254: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/unidevelopment/sadyba-spot/#/",
        defaultOptions: {
            app: 0,
            b: "A",
            lang: "pl"
        },
        visualisationApp: 1,
        yslabOfferId: 503
    },
    16506: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/neocity/neopolis/#/",
        defaultOptions: {
            app: 0,
            b: "C",
            lang: "pl"
        },
        visualisationApp: 1,
        yslabOfferId: 124
    },
    16540: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/atal/osiedle-poematu/#/",
        defaultOptions: {
            app: 0,
            b: "P9",
            lang: "pl"
        },
        visualisationApp: 5,
        yslabOfferId: 483
    },
    17164: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/sgi/lopuszanska/#/",
        defaultOptions: {
            app: 0,
            lang: "pl"
        },
        visualisationApp: 1,
        yslabOfferId: 457
    },
    17290: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/atal/osiedle-poematu/#/",
        defaultOptions: {
            app: 0,
            b: "P10",
            lang: "pl"
        },
        visualisationApp: 5,
        yslabOfferId: 483
    },
    15937: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/asbud/nowa-praga/#/buildings/B9",
        yslabOfferId: 481
    },
    16894: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/bouygues-immobilier-polska/lumea-e4/#/buildings/BABBBDBC",
        yslabOfferId: 521
    },
    16746: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/bouygues-immobilier-polska/reve/#/",
        yslabOfferId: 532
    },
    15989: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/ronson/ursus-centralny/#/buildings/11",
        yslabOfferId: 136
    },
    16661: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/domd/harmonia-mokotow/#/",
        yslabOfferId: 540
    },
    16662: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/domd/metro-zachod/#/buildings/A10",
        yslabOfferId: 213,
        allowComparisonBasedOnConfiguredBuildingName: true
    },
    13879: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/domd/urbino/#/",
        yslabOfferId: 312,
        buildingNameMap: {A2: "A"}
    },
    16139: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/domd/osiedle-przy-alejach/#/buildings/A",
        yslabOfferId: 492
    }
};

export type Planner3DYslabDefinition = {
    type: Planner3DTypes.YSLAB;
    plannerUrl: string;
    yslabOfferId: number;
    defaultBuilding?: never;
    defaultOptions?: never;
    visualisationApp?: never;
    allowComparisonBasedOnConfiguredBuildingName?: boolean;
    buildingNameMap?: Record<string, string>;
};

export type Planner3DYslabAllInOneDefinition = {
    type: Planner3DTypes.YSLAB_ALLINONE;
    plannerUrl: string;
    /**
     * Yslab offer id is copied from api https://api.resimo.pl/api/v1/ops/rp/investments?token=7GK4EPxhYHi0AMs0R1QHPGIzc
     */
    yslabOfferId: number;
    /**
     * Default options for all-in-one yslab app. Will be used in application url search params
     */
    defaultOptions?: Record<string, string | number>;
    visualisationApp?: number;
    allowComparisonBasedOnConfiguredBuildingName?: never;
    buildingNameMap?: never;
};

export type Planner3DDefinitions = Planner3DYslabDefinition | Planner3DYslabAllInOneDefinition;
import {YslabJeffAppApartment} from "../detail/planner3D/components/types/YslabEventTypes";

export enum Planner3DTypes {
    YSLAB,
    YSLAB_ALLINONE
}

export const getPlanner3dData = (offerId: number) => planner3dOfferMap[offerId] || null;

/**
 * RP and YSLAB may have different ways of naming properties. By default before comparison we strip characters other than letters and numbers
 */
export const compareJeffProperties =
    (
        apartment: Pick<YslabJeffAppApartment, "configuredBuildingName" | "buildingNumber" | "mappingName">,
        allowComparisonBasedOnConfiguredBuildingName = false,
        /**
         * @param buildingNameMap - remaps yslab buildingNumber to the ones used in our apartment numbers
         */
        buildingNameMap?: Record<string, string>
    ) =>
    (p: {number: string}) => {
        const {configuredBuildingName, buildingNumber, mappingName} = apartment;
        // match buildings
        let isBuildingMatched = !Boolean(buildingNumber);
        const canUseConfiguredBuildingName = allowComparisonBasedOnConfiguredBuildingName || (configuredBuildingName && !buildingNumber);
        const remappedBuildingNumber = (buildingNumber && buildingNameMap?.[buildingNumber]) || buildingNumber;

        if (remappedBuildingNumber || configuredBuildingName) {
            const rpBuildingMatch =
                (!!remappedBuildingNumber && p.number.match(remappedBuildingNumber)) ||
                (canUseConfiguredBuildingName && !!configuredBuildingName && p.number.match(configuredBuildingName));
            isBuildingMatched = Boolean(rpBuildingMatch);
        }
        // match apartments
        const rpApartmentNumber = stripJeffNamesToLastNumber(remappedBuildingNumber ? p.number.replace(remappedBuildingNumber, "") : p.number);
        const mappingNameWithoutBuilding = remappedBuildingNumber ? mappingName.replace(remappedBuildingNumber, "") : mappingName;
        const yslabApartmentNumber = stripJeffNamesToLastNumber(mappingNameWithoutBuilding);
        return isBuildingMatched && yslabApartmentNumber?.toLowerCase() === rpApartmentNumber.toLowerCase();
    };
const stripJeffNamesToLastNumber = (str: string) => {
    const match = str.match(/[0-9]*$/);
    // remove leading zeros
    return match ? parseInt(match[0], 10).toString() : str;
};

const planner3dOfferMap: Record<number, Planner3DDefinitions> = {
    16254: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/unidevelopment/sadyba-spot/#/",
        defaultOptions: {
            app: 0,
            b: "A",
            lang: "pl"
        },
        visualisationApp: 1,
        yslabOfferId: 503
    },
    16506: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/neocity/neopolis/#/",
        defaultOptions: {
            app: 0,
            b: "C",
            lang: "pl"
        },
        visualisationApp: 1,
        yslabOfferId: 124
    },
    16540: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/atal/osiedle-poematu/#/",
        defaultOptions: {
            app: 0,
            b: "P9",
            lang: "pl"
        },
        visualisationApp: 5,
        yslabOfferId: 483
    },
    17164: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/sgi/lopuszanska/#/",
        defaultOptions: {
            app: 0,
            lang: "pl"
        },
        visualisationApp: 1,
        yslabOfferId: 457
    },
    17290: {
        type: Planner3DTypes.YSLAB_ALLINONE,
        plannerUrl: "https://allinone-rp.prod.resimo.io/atal/osiedle-poematu/#/",
        defaultOptions: {
            app: 0,
            b: "P10",
            lang: "pl"
        },
        visualisationApp: 5,
        yslabOfferId: 483
    },
    15937: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/asbud/nowa-praga/#/buildings/B9",
        yslabOfferId: 481
    },
    16894: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/bouygues-immobilier-polska/lumea-e4/#/buildings/BABBBDBC",
        yslabOfferId: 521
    },
    16746: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/bouygues-immobilier-polska/reve/#/",
        yslabOfferId: 532
    },
    15989: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/ronson/ursus-centralny/#/buildings/11",
        yslabOfferId: 136
    },
    16661: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/domd/harmonia-mokotow/#/",
        yslabOfferId: 540
    },
    16662: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/domd/metro-zachod/#/buildings/A10",
        yslabOfferId: 213,
        allowComparisonBasedOnConfiguredBuildingName: true
    },
    13879: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/domd/urbino/#/",
        yslabOfferId: 312,
        buildingNameMap: {A2: "A"}
    },
    16139: {
        type: Planner3DTypes.YSLAB,
        plannerUrl: "https://v4-jeff-rp.prod.resimo.io/domd/osiedle-przy-alejach/#/buildings/A",
        yslabOfferId: 492
    }
};

export type Planner3DYslabDefinition = {
    type: Planner3DTypes.YSLAB;
    plannerUrl: string;
    yslabOfferId: number;
    defaultBuilding?: never;
    defaultOptions?: never;
    visualisationApp?: never;
    allowComparisonBasedOnConfiguredBuildingName?: boolean;
    buildingNameMap?: Record<string, string>;
};

export type Planner3DYslabAllInOneDefinition = {
    type: Planner3DTypes.YSLAB_ALLINONE;
    plannerUrl: string;
    /**
     * Yslab offer id is copied from api https://api.resimo.pl/api/v1/ops/rp/investments?token=7GK4EPxhYHi0AMs0R1QHPGIzc
     */
    yslabOfferId: number;
    /**
     * Default options for all-in-one yslab app. Will be used in application url search params
     */
    defaultOptions?: Record<string, string | number>;
    visualisationApp?: number;
    allowComparisonBasedOnConfiguredBuildingName?: never;
    buildingNameMap?: never;
};

export type Planner3DDefinitions = Planner3DYslabDefinition | Planner3DYslabAllInOneDefinition;
